/**
 * @file PaginationControls.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-28
 *
 * 1.1.0: `pagination.from` / `pagination.to` may be given when pages are not
 * all `limit` long (Pricing & Costing's pages of whole product groups).
 *
 * 1.2.0 (2026-10-28, Cam — rep mobile review): on a rep's phone the pager is
 * Previous and Next as 44px buttons with "Page 1 of 410" between them. The
 * desktop's arrows were 18px wide and the page box 30 × 23 — too small to hit
 * with a thumb. The desktop pager is unchanged.
 */

import useRepMobile from '../hooks/useRepMobile';
import './SearchContacts.css';
import './PaginationControls.css';

function PaginationControls({ pagination, onPageChange }) {
  const { page, limit, total, totalPages, from, to } = pagination;
  const repMobile = useRepMobile();

  const handlePageInput = (value) => {
    const pageNum = Math.min(totalPages, Math.max(1, parseInt(value) || 1));
    onPageChange(pageNum);
  };

  if (repMobile) {
    const first = from ?? (((page - 1) * limit) + 1);
    const last = to ?? Math.min(page * limit, total);
    const pages = Math.max(1, totalPages || 1);
    return (
      <div className="pagination-controls rep-pager">
        <button
          type="button"
          data-read-safe
          className="rep-pager-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <span className="material-symbols-rounded" aria-hidden="true">chevron_left</span>
          Previous
        </button>
        <div className="rep-pager-info" aria-live="polite">
          <span className="rep-pager-page">Page {page} of {pages}</span>
          <span className="rep-pager-count">{first}–{last} of {total}</span>
        </div>
        <button
          type="button"
          data-read-safe
          className="rep-pager-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pages}
        >
          Next
          <span className="material-symbols-rounded" aria-hidden="true">chevron_right</span>
        </button>
      </div>
    );
  }

  return (
    <div className="pagination-controls">
      <div className="pagination-info">
        Showing {from ?? (((page - 1) * limit) + 1)} to {to ?? Math.min(page * limit, total)} of {total}
      </div>
      
      <div className="pagination-nav">
        <button 
          onClick={() => onPageChange(1)}
          disabled={page === 1}
          className="btn-icon"
        >
          <span className="material-symbols-rounded">keyboard_double_arrow_left</span>
        </button>
        
        <button 
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="btn-icon"
        >
          <span className="material-symbols-rounded">keyboard_arrow_left</span>
        </button>
        
        <div className="page-input-group">
          <span>Page</span>
          <div className="custom-number-input">
            <input
              type="text"
              value={page}
              onChange={(e) => handlePageInput(e.target.value)}
              onBlur={(e) => handlePageInput(e.target.value)}
              className="page-number-input"
            />
          </div>
          <span>of {totalPages}</span>
        </div>
        
        <button 
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="btn-icon"
        >
          <span className="material-symbols-rounded">keyboard_arrow_right</span>
        </button>
        
        <button 
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
          className="btn-icon"
        >
          <span className="material-symbols-rounded">keyboard_double_arrow_right</span>
        </button>
      </div>
    </div>
  );
}

export default PaginationControls;